import {action, makeObservable, observable} from "mobx";
import { executeAutomation, findAutomations, toForm } from "../helpers/automations/helpers";
import { ITransactionFormState } from "../components/Transactions/Components/Form/TransactionForm";
import { IAutomation } from "../config/automation";
import { apiV1 } from "../services/api";
import { convertFromResponse } from "../validators/automations";

const AUTOMATIONS_CACHE_KEY = 'em:user:automations';

export class AutomationStore {
    @observable automations: IAutomation[] = [];

    constructor() {
        makeObservable(this);

        const data = window.localStorage.getItem(AUTOMATIONS_CACHE_KEY);
        if (!data) {
            return;
        }

        this.automations = JSON.parse(data);
    }

    @action
    load(automations: IAutomation[]) {
        this.automations = automations;
        this.updateLocalStorage();
    }


    @action
    addAutomation(automation: IAutomation) {
        this.automations.push(automation);
        this.updateLocalStorage();
    }

    @action
    replaceAutomation(automation: IAutomation) {
        const index = this.automations.findIndex(item => item.id === automation.id);
        if (index === -1) {
            return;
        }

        this.automations[index] = automation;
        this.updateLocalStorage();
    }

    @action
    removeAutomation(automation: IAutomation) {
        this.automations = this.automations.filter(item => item.id !== automation.id);
        this.updateLocalStorage();
    }

    exec(field: keyof ITransactionFormState, value: any): ITransactionFormState | {} {
        const automations = this.automations.filter(findAutomations(field, value));
        if (!automations.length) {
            return {};
        }

        return toForm(automations.map(executeAutomation));
    }

    private updateLocalStorage() {
        window.localStorage.setItem(AUTOMATIONS_CACHE_KEY, JSON.stringify(this.automations));
    }
}

export const fetchAutomations = (): Promise<IAutomation[]> => {
    return apiV1.GET( '/automations')
        .then(response => response.json())
        .then(data => data.data);
};

export const createAutomation = (data: IAutomation): Promise<IAutomation> => {
    return apiV1.POST(`/automations`, data)
        .then((response: any) => {
            if (response.status !== 201) {
                return response.json().then((reason: any) => {
                    if (reason.errors === undefined) {
                        return Promise.reject(reason);
                    }

                    return Promise.reject(convertFromResponse(reason.errors));
                });
            }

            return Promise.resolve({
                id: parseInt(response.headers.get('Location').replace(/.*\/([0-9]+)$/, '$1')),
                type: data.type,
                value: data.value,
            });
        });
};

export const updateAutomation = (data: IAutomation): Promise<IAutomation> => {
    return apiV1.PATCH(`/automations/${data.id}`, data)
        .then((response: any) => {
            if (response.status !== 200) {
                return response.json().then((reason: any) => {
                    if (reason.errors === undefined) {
                        return Promise.reject(reason);
                    }

                    return Promise.reject(convertFromResponse(reason.errors));
                });
            }

            return response.json();
        })
        .then(data => data.data);
};

export const deleteAutomation = (id: number): Promise<boolean> => {
    return apiV1.DELETE(`/automations/${id}`);
};
